import React, { useState, useEffect } from 'react';
import { 
  Tr, 
  Td, 
  Text, 
  Badge,
  Flex,
  Box,
  Tooltip
} from '@chakra-ui/react';
import api from '../../services/api';
import {
  StatusCell,
  IdCell,
  TaskDateCell,
  TitleCell,
  PriorityCell,
  TaskAssigneeCell,
  DueDateCell,
} from './TasksCellRenderers';
import { TaskActions, StatusToggle } from './TasksRowActions';
import { formatUserEmail, formatRelativeTime } from './TasksUtils';

/**
 * Component for rendering a group of tasks belonging to the same lead
 */
const TasksGroupedRow = ({ tasks, onStatusChange, onTitleChange }) => {
  const [users, setUsers] = useState([]);
  const lead = tasks[0].leadDetails;
  
  // Fetch all users to map UIDs to emails
  useEffect(() => {
    let isMounted = true;
    
    const fetchUsers = async () => {
      try {
        const response = await api.get('/api/users');
        if (isMounted && response.data && response.data.users) {
          setUsers(response.data.users);
        }
      } catch (error) {
        console.error('[TasksGroupedRow] Error fetching users:', error);
      }
    };
    
    fetchUsers();
    
    return () => {
      isMounted = false;
    };
  }, []);
  
  return (
    <>
      {/* Group header row with lead information */}
      <Tr bg="gray.50" className="group-header">
        <Td colSpan={11} py={1} px={2}>
          <Flex align="center">
            <TaskActions task={tasks[0]} isHeader={true} />
            <Flex mr={2} alignItems="center">
              <Text fontWeight="medium" mr={1}>{tasks[0].leadId || 'No ID'}</Text>
              <Tooltip label={lead?.brideAndGroomName || "Unknown"}>
                <Text fontWeight="medium" minW="130px" maxW="240px" w="240px" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                  {lead?.brideAndGroomName || "Unknown"}
                </Text>
              </Tooltip>
              <Badge ml={2} colorScheme="blue">{tasks.length} tasks</Badge>
            </Flex>
            {lead?.nextFollowUpTime && (
              <Text fontSize="xs" color="orange.500" fontWeight="medium" mx={2}>
                Follow Up: {new Date(lead.nextFollowUpTime) > new Date() ? 'in ' : ''}
                {formatRelativeTime(lead.nextFollowUpTime)}
              </Text>
            )}
            <Text fontSize="xs" color="gray.600" mx={4}>
              Assigned to: {tasks[0]?.assignedTo?.email ? formatUserEmail(tasks[0].assignedTo.email) : "Unassigned"}
            </Text>
          </Flex>
        </Td>
      </Tr>
      
      {/* Individual task rows with 100px left and right padding */}
      {tasks.map((task, index) => (
        <Tr 
          key={task._id} 
          className="grouped-task-row"
          _hover={{ bg: 'gray.100' }}
          borderLeftWidth="4px"
          borderLeftColor="blue.200"
          borderLeftStyle="solid"
          sx={{ '& > td:first-of-type': { pl: '100px' }, '& > td:last-of-type': { pr: '100px' } }}
        >
          {/* Status toggle */}
          <StatusToggle task={task} onStatusChange={onStatusChange} />
  
          {/* Task Title with edit capability */}
          <TitleCell task={task} onTitleChange={onTitleChange} />
          
          {/* Priority */}
          <PriorityCell task={task} />
          
          {/* Task Assignee with edit capability */}
          <TaskAssigneeCell task={task} users={users} onStatusChange={onStatusChange} />
          
          {/* Due Date with edit capability */}
          <DueDateCell task={task} onStatusChange={onStatusChange} />
          
          {/* Empty cell for actions since they're in the header */}
          <Td py="1" px="0"></Td>
        </Tr>
      ))}
      
      {/* Optional visual separator after the group */}
      <Tr className="group-separator">
        <Td colSpan={11} py={0} px={0}>
          <Box h="4px" bg="white"></Box>
        </Td>
      </Tr>
    </>
  );
};

export default TasksGroupedRow;
